import { initializeApp } from 'firebase/app';
import {
  getAuth,
  signInWithPopup,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  onIdTokenChanged,
  GoogleAuthProvider,
  type User,
} from 'firebase/auth';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
};

// Distinct Firebase app instance name for the staff console.
// Keeps IndexedDB auth persistence isolated from the customer-facing apps
// so signing out of staff.optisigns.com does not clear app.optisigns.com.
const FIREBASE_APP_NAME = '___staff_auth___';

const app = initializeApp(firebaseConfig, FIREBASE_APP_NAME);
export const auth = getAuth(app);

// Resolves when Firebase determines initial auth state from IndexedDB.
// First fire may be null (pre-hydration); that's fine for "signed out" UX.
// `waitForAuthUser` (below) is what API callers should await — it resolves
// on the FIRST fire with a user, or on the first null fire after a short
// delay (so we don't hang forever when there's genuinely no session).
export const authInitialized = new Promise<void>((resolve) => {
  const unsubscribe = onAuthStateChanged(auth, () => {
    resolve();
    unsubscribe();
  });
});

/**
 * Resolves with the first non-null Firebase user, or with null after a short
 * grace period if the user never hydrates. Prevents the race where
 * `getTokenString` is called between Firebase SDK init and IndexedDB hydrate
 * — which would otherwise return null and leave Apollo to send an empty
 * Bearer header, which the backend rejects with API_TOKEN_INVALID.
 */
const AUTH_HYDRATE_GRACE_MS = 1500;
export const authUserReady = new Promise<User | null>((resolve) => {
  if (auth.currentUser) {
    resolve(auth.currentUser);
    return;
  }
  let settled = false;
  const settle = (value: User | null) => {
    if (settled) return;
    settled = true;
    unsubscribe();
    resolve(value);
  };
  const unsubscribe = onAuthStateChanged(auth, (user) => {
    if (user) settle(user);
  });
  setTimeout(() => settle(auth.currentUser ?? null), AUTH_HYDRATE_GRACE_MS);
});

// Providers — staff uses Google Workspace only (@optisigns.com hosted domain).
export const googleProvider = new GoogleAuthProvider();
googleProvider.setCustomParameters({ hd: 'optisigns.com' });

// Get fresh token with auto-refresh if expiring within 3 minutes.
export async function getFreshToken(forceRefresh = false): Promise<{
  token: string;
  expires: string;
  uid: string;
}> {
  const user = auth.currentUser;
  if (!user) throw new Error('No authenticated user');

  const tokenResult = await user.getIdTokenResult(forceRefresh);
  const expires = tokenResult.expirationTime;

  const diff = (new Date(expires).getTime() - Date.now()) / 60000;
  if (diff < 3) {
    const refreshed = await user.getIdTokenResult(true);
    return {
      token: refreshed.token,
      expires: refreshed.expirationTime,
      uid: user.uid,
    };
  }

  return { token: tokenResult.token, expires, uid: user.uid };
}

export {
  signInWithPopup,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  onIdTokenChanged,
  GoogleAuthProvider,
};

// Convenience wrapper: extracts token string for Apollo auth callbacks.
// Awaits `authUserReady` (not just the first auth fire) so the first
// request after boot doesn't race IndexedDB hydration.
export async function getTokenString(): Promise<string | null> {
  const user = await authUserReady;
  if (!user) return null;
  try {
    const result = await getFreshToken();
    return result.token;
  } catch {
    return null;
  }
}

export type { User };
